/**
 * Marquee types the API does not describe: FE view models, the option lists the
 * forms offer, and display-side classifications.
 *
 * Response and request shapes are not here. They come from the generated SDK
 * and call sites import them straight from `@/sdk`; this file does not re-export
 * them. If a field is missing, it is a backend gap to report, not a shape to
 * invent (see .claude/rules/sdk.md).
 */
import type {
	MarqueeCaptureMode,
	MarqueeEventFormat,
	MarqueeEventType,
	MarqueeRsvpStatus
} from '@/sdk'

// ─── Form option lists ──────────────────────────────────────────────────────

/*
 * The three event dimensions are independent and only one of them leaves the
 * tool:
 * - `format` is the Salesforce Activity Type picklist and the only cell of the
 *   export CSV we decide (Status, RecordTypeId and Result are hardcoded);
 * - `capture_mode` decides how CTAs are captured, and never reaches Salesforce;
 * - `event_type` is how the room was attended, and never reaches Salesforce
 *   either.
 * Each gets its own control, so no single widget writes two fields.
 */

/**
 * Salesforce Activity Type picklist, ordered for the form's select: the two
 * Marquee runs first, then the rest of the picklist for events created
 * elsewhere. The list is the API's own enum — every value is selectable, so an
 * edit can never be forced to rewrite a format the operator did not choose.
 *
 * No default is prefilled: the field is optional and the select opens on its
 * placeholder, so an untouched create omits it and the backend applies its own
 * default (`Conference`).
 */
export const MARQUEE_EVENT_FORMAT_OPTIONS = [
	'Conference',
	'Market Visit',
	'Meeting',
	'Call',
	'Email',
	'SMS',
	'Other'
] as const satisfies readonly MarqueeEventFormat[]

/**
 * Capture modes, in control order (default first). `presentation` snapshots one
 * event-level CTA selection onto everyone who checks in; `tune_up` is the 1:1
 * format where each attendee would carry their own set.
 *
 * Chosen at create and **immutable afterwards** — `MarqueeEventUpdate` carries
 * no `capture_mode`, which is how the contract says so.
 */
export const MARQUEE_CAPTURE_MODE_OPTIONS = [
	'presentation',
	'tune_up'
] as const satisfies readonly MarqueeCaptureMode[]

/**
 * The invite answers the manual-add form offers, matching what the invitee file
 * can actually express (`Sent` / `Attending` / `Not Attending`).
 *
 * `no_response` is deliberately absent: it is the closing verdict the lock
 * writes over every `invited` row, so before the lock there is nothing for it to
 * mean, and the CSV vocabulary cannot produce it either.
 */
export const MARQUEE_RSVP_ENTRY_OPTIONS = [
	'invited',
	'rsvp_yes',
	'declined'
] as const satisfies readonly MarqueeRsvpStatus[]

/** Capture mode prefilled by the create flow; mirrors the backend's default. */
export const MARQUEE_DEFAULT_CAPTURE_MODE: MarqueeCaptureMode = 'presentation'

/**
 * Event types selectable in the form. Two options only: `null` is a read-only
 * state (events created before the field existed), so it is rendered wherever
 * an event is displayed but never offered as a choice.
 */
export const MARQUEE_EVENT_TYPE_OPTIONS = [
	'in_person',
	'virtual'
] as const satisfies readonly MarqueeEventType[]

/** Event type prefilled by the create flow; mirrors the backend's default. */
export const MARQUEE_DEFAULT_EVENT_TYPE: MarqueeEventType = 'in_person'

// ─── Operator permissions ───────────────────────────────────────────────────

/**
 * The id of an operation the UI can ask permission for.
 *
 * `MarqueeOperator.permitted` is a bare `string[]` on the wire, and deliberately
 * so: an enum would make every verb the backend adds later a breaking contract
 * change. What the array carries are **operationIds**, which this generator
 * emits as the SDK function names, so the module's own export names are the
 * vocabulary: no hand-kept list of operation strings, and a renamed or
 * misspelled operation stops compiling here rather than silently gating nothing.
 *
 * Wider than the 32 Marquee operations (the barrel exports a few non-operations
 * too), which costs nothing: the point is to catch a name that exists nowhere,
 * not to re-derive the role map the backend owns.
 */
export type MarqueeOperationId = keyof typeof import('@/sdk')

// ─── Display-side classifications ───────────────────────────────────────────

/**
 * Host tier classification shown on attendees (display-only).
 *
 * The API types `segment` as a free string ("stored as-is" from the invitee
 * import), so this is the set the UI knows how to label, not a guarantee about
 * the wire value. Anything else falls back to the raw string.
 */
export type MarqueeSegment = 'prohost' | 'core'

/** The two segments the invitee template uses, offered by the manual-add form. */
export const MARQUEE_SEGMENT_OPTIONS = [
	'prohost',
	'core'
] as const satisfies readonly MarqueeSegment[]

/**
 * The status a Team row shows, derived from `active`, `activation_pending` and
 * `activation_expires_at` by `teamStatus`: the API carries no status field of
 * its own.
 */
export const MARQUEE_TEAM_STATUS_OPTIONS = [
	'active',
	'invited',
	'invite_expired',
	'deactivated'
] as const

export type MarqueeTeamStatus = (typeof MARQUEE_TEAM_STATUS_OPTIONS)[number]

/** The status a Locations row shows, read off `active`. */
export const MARQUEE_LOCATION_STATUS_OPTIONS = [
	'active',
	'deactivated'
] as const

export type MarqueeLocationStatus =
	(typeof MARQUEE_LOCATION_STATUS_OPTIONS)[number]
